import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../admin/auth/AuthContext';
import AdminLayout from '../components/admin/AdminLayout';

/**
 * Admin Shell - Entry point for /admin routes
 * Verifies session and renders admin UI or redirects to login
 */
export default function AdminShell({ children }) {
  const navigate = useNavigate();
  const { state, verifySessionOnce } = useAuth();
  const redirecting = useRef(false);

  useEffect(() => {
    // Verify session on mount
    verifySessionOnce();
  }, [verifySessionOnce]);

  useEffect(() => {
    // Listen for auth expiration events
    const handleAuthExpired = () => {
      if (redirecting.current) return;
      redirecting.current = true;
      navigate('/admin-panel/login?reason=expired', { replace: true });
    };

    window.addEventListener('bm_admin_auth_expired', handleAuthExpired);

    return () => {
      window.removeEventListener('bm_admin_auth_expired', handleAuthExpired);
    };
  }, [navigate]);

  useEffect(() => {
    // Redirect to login if not authenticated
    if (state.sessionOk === false && !redirecting.current) {
      redirecting.current = true;
      navigate('/admin-panel/login', { replace: true });
    }
  }, [state.sessionOk, navigate]);

  // Loading state - show skeleton instead of blank screen
  if (state.loading || state.sessionOk === null) {
    return (
      <div className="min-h-screen bg-[#f8fafc] flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-blue-500 border-r-transparent mb-4"></div>
          <div className="text-slate-600">Verifying access...</div>
        </div>
      </div>
    );
  }

  // Not authenticated
  if (state.sessionOk === false) {
    return null; // Will redirect via useEffect
  }

  // Authenticated - render admin layout with children
  return (
    <AdminLayout>
      {children}
    </AdminLayout>
  );
}
